/**
 * Service orders list — "Meus pedidos", the design brief's Home screen and
 * the portal's actual reason to exist.
 *
 * Cursor-paginated through `api/serviceOrders.ts`, newest-first (the API's
 * own ordering; this page does not re-sort). Three outcomes, and — same
 * discipline as `NotificacoesList` — they must not be confused with one
 * another:
 *   - a genuinely empty account (zero orders) → the existing `EmptyState`
 *   - the fetch failed                        → an honest error, never
 *                                                 rendered as "no orders"
 *   - rows                                     → one `ServiceOrderCard` each
 */
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';

import { EmptyState } from '../components/EmptyState.tsx';
import { ServiceOrderCard } from '../components/ServiceOrderCard.tsx';
import { IconAlert, IconOrders } from '../components/icons.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { listServiceOrders, type ListServiceOrdersResult } from '../api/serviceOrders.ts';
import styles from './OrdensServicoList.module.css';
import { runtimeEnv } from '../runtimeConfig.ts';

const PAGE_SIZE = 20;

export function OrdensServicoList(): React.JSX.Element {
  const { t } = useTranslation();
  const auth = useAuth();
  const accessToken = auth.session?.access_token;
  const bffBase = runtimeEnv('VITE_BFF_BASE_URL') ?? '/bff/v1';

  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = useInfiniteQuery({
    queryKey: ['service-orders', 'list'],
    queryFn: async ({ pageParam }): Promise<ListServiceOrdersResult> => {
      const result = await listServiceOrders(fetch, bffBase, accessToken ?? '', pageParam, PAGE_SIZE);
      if (result.error) throw new Error(result.error);
      return result;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: Boolean(accessToken),
  });

  if (isLoading) {
    return <p className={styles['status']}>{t('orders.loading')}</p>;
  }

  if (isError) {
    return (
      <div className="ns-alert" role="alert">
        <IconAlert size={18} />
        <div>
          <p className={styles['errorTitle']}>{t('orders.loadErrorTitle')}</p>
          <p className={styles['errorBody']}>{t('orders.loadErrorBody')}</p>
          <button type="button" className={`ns-cta ${styles['errorRetry']}`} onClick={() => void refetch()}>
            {t('orders.retry')}
          </button>
        </div>
      </div>
    );
  }

  const orders = data?.pages.flatMap((page) => page.data) ?? [];

  if (orders.length === 0) {
    return (
      <EmptyState
        icon={<IconOrders size={32} />}
        title={t('empty.orders.title')}
        description={t('empty.orders.body')}
      />
    );
  }

  return (
    <div className={styles['page']}>
      <p className={`ns-display ${styles['title']}`}>{t('orders.pageTitle')}</p>

      <ul className={styles['list']}>
        {orders.map((order) => (
          <ServiceOrderCard key={order.order_id} order={order} />
        ))}
      </ul>

      {hasNextPage && (
        <button
          type="button"
          className={`ns-cta ${styles['loadMore']}`}
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
        >
          {isFetchingNextPage ? t('orders.loadingMore') : t('orders.loadMore')}
        </button>
      )}
    </div>
  );
}
